import React, {useEffect, useMemo, useState} from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    TextField,
    Typography
} from "@mui/material";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import DownloadIcon from "@mui/icons-material/Download";
import {axiosRun} from "../utils/axiosWrap";
import {config} from "../config";

const getDownloadUrl = (csvUrl) => {
    if (!csvUrl) return "";

    const apiUrl = new URL(config.apiUrl);
    return `${apiUrl.origin}${csvUrl}`;
};

const downloadCsvContent = (csv) => {
    if (!csv?.content) return false;

    const blob = new Blob([csv.content], {type: "text/csv;charset=utf-8"});
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = csv.fileName || "corp-emails.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return true;
};

export const CorporateEmailRegistration = () => {
    const [faculties, setFaculties] = useState([]);
    const [formData, setFormData] = useState({
        facultyId: "",
        importYear: "2026",
        limit: "",
        orgUnitPath: "/Students"
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);

    const downloadUrl = useMemo(() => getDownloadUrl(result?.csv?.url), [result]);

    useEffect(() => {
        const loadFaculties = async () => {
            try {
                const data = await axiosRun(config.apiLinks.getFacultiesListClear, "GET");
                setFaculties(data);
            } catch (loadError) {
                setError(loadError.data?.message || "Не вдалося завантажити факультети");
            }
        };

        loadFaculties();
    }, []);

    const changeField = (name, value) => {
        setFormData((prevState) => ({
            ...prevState,
            [name]: value
        }));
    };

    const startRegistration = async () => {
        setIsLoading(true);
        setError(null);
        setResult(null);

        try {
            const body = {
                facultyId: formData.facultyId,
                importYear: formData.importYear,
                limit: formData.limit,
                orgUnitPath: formData.orgUnitPath
            };
            const data = await axiosRun(config.apiLinks.registerCorpEmails, "POST", body);
            setResult(data);
            downloadCsvFile(data.csv);
        } catch (runError) {
            setError(runError.data?.message || runError.data?.error || "Не вдалося запустити реєстрацію корпоративних пошт");
        } finally {
            setIsLoading(false);
        }
    };

    const downloadCsvFile = (csv) => {
        if (downloadCsvContent(csv)) return;
        if (!csv?.url) return;

        const link = document.createElement("a");
        link.href = getDownloadUrl(csv.url);
        link.download = csv.fileName || "corp-emails.csv";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const downloadCsv = () => {
        downloadCsvFile(result?.csv);
    };

    const isRunDisabled = isLoading || !formData.facultyId || !String(formData.importYear).trim();

    return (
        <Paper sx={{p: 3, minHeight: "85vh"}}>
            <Typography variant="h4" component="h1" gutterBottom>
                Корпоративні пошти
            </Typography>

            <Grid container spacing={2} sx={{mt: 1, maxWidth: 900}}>
                <Grid item xs={12} md={6}>
                    <FormControl fullWidth required>
                        <InputLabel id="corp-email-faculty-label">Факультет</InputLabel>
                        <Select
                            labelId="corp-email-faculty-label"
                            label="Факультет"
                            value={formData.facultyId}
                            onChange={(event) => changeField("facultyId", event.target.value)}
                        >
                            {faculties.map((faculty) => (
                                <MenuItem value={faculty.id} key={faculty.id}>
                                    {faculty.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        fullWidth
                        required
                        label="Частина importHash"
                        value={formData.importYear}
                        onChange={(event) => changeField("importYear", event.target.value)}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        fullWidth
                        type="number"
                        label="Ліміт"
                        value={formData.limit}
                        onChange={(event) => changeField("limit", event.target.value)}
                        inputProps={{min: 0}}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="Google orgUnitPath"
                        value={formData.orgUnitPath}
                        onChange={(event) => changeField("orgUnitPath", event.target.value)}
                    />
                </Grid>
            </Grid>

            <Box sx={{mt: 3, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap"}}>
                <Button
                    variant="contained"
                    startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : <ForwardToInboxIcon />}
                    disabled={isRunDisabled}
                    onClick={startRegistration}
                >
                    Запустити
                </Button>

                {downloadUrl && (
                    <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={downloadCsv}
                    >
                        Завантажити CSV
                    </Button>
                )}
            </Box>

            {error && (
                <Alert severity="error" sx={{mt: 3}}>
                    {error}
                </Alert>
            )}

            {result && (
                <Paper variant="outlined" sx={{mt: 3, p: 2, maxWidth: 900, backgroundColor: "#fff"}}>
                    <Typography variant="subtitle1" gutterBottom>
                        Результат
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={6} md={3}>
                            <Typography variant="body2" color="text.secondary">Режим</Typography>
                            <Typography variant="body1">{result.mode}</Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <Typography variant="body2" color="text.secondary">Користувачі</Typography>
                            <Typography variant="body1">{result.foundUsers}</Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <Typography variant="body2" color="text.secondary">Записи</Typography>
                            <Typography variant="body1">{result.processed}</Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <Typography variant="body2" color="text.secondary">CSV рядки</Typography>
                            <Typography variant="body1">{result.csv?.rows}</Typography>
                        </Grid>
                    </Grid>
                </Paper>
            )}
        </Paper>
    );
};
